import { createApp, reactive } from 'vue';
import router from '@/router/index';
import AppWrapper from '@/components/AppWrapper.vue';
import PrimeVue from 'primevue/config';
import autocomplete from 'primevue/autocomplete';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';
import Badge from 'primevue/badge';
import BadgeDirective from 'primevue/badgedirective';
import Button from 'primevue/button';
import Breadcrumb from 'primevue/breadcrumb';
import Calendar from 'primevue/calendar';
import Card from 'primevue/card';
import Carousel from 'primevue/carousel';
import Chart from 'primevue/chart';
import Checkbox from 'primevue/checkbox';
import Chip from 'primevue/chip';
import Chips from 'primevue/chips';
import ColorPicker from 'primevue/colorpicker';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import ConfirmDialog from 'primevue/confirmdialog';
import ConfirmPopup from 'primevue/confirmpopup';
import ConfirmationService from 'primevue/confirmationservice';
import ContextMenu from 'primevue/contextmenu';
import DataTable from 'primevue/datatable';
import DataView from 'primevue/dataview';
import DataViewLayoutOptions from 'primevue/dataviewlayoutoptions';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import Dropdown from 'primevue/dropdown';
import Editor from 'primevue/editor';
import EventBus from '@/utilities/event-bus';
import Fieldset from 'primevue/fieldset';
import FileUpload from 'primevue/fileupload';
import Galleria from 'primevue/galleria';
import Image from 'primevue/image';
import InlineMessage from 'primevue/inlinemessage';
import Inplace from 'primevue/inplace';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import Knob from 'primevue/knob';
import Listbox from 'primevue/listbox';
import MegaMenu from 'primevue/megamenu';
import Menu from 'primevue/menu';
import Menubar from 'primevue/menubar';
import Message from 'primevue/message';
import MultiSelect from 'primevue/multiselect';
import OrderList from 'primevue/orderlist';
import OrganizationChart from 'primevue/organizationchart';
import OverlayPanel from 'primevue/overlaypanel';
import Paginator from 'primevue/paginator';
import Panel from 'primevue/panel';
import PanelMenu from 'primevue/panelmenu';
import Password from 'primevue/password';
import PickList from 'primevue/picklist';
import ProgressBar from 'primevue/progressbar';
import ProgressSpinner from 'primevue/progressspinner';
import Rating from 'primevue/rating';
import RadioButton from 'primevue/radiobutton';
import Row from 'primevue/row';
import Ripple from 'primevue/ripple';
import SelectButton from 'primevue/selectbutton';
import ScrollPanel from 'primevue/scrollpanel';
import ScrollTop from 'primevue/scrolltop';
import Slider from 'primevue/slider';
import Sidebar from 'primevue/sidebar';
import Skeleton from 'primevue/skeleton';
import SpeedDial from 'primevue/speeddial';
import SplitButton from 'primevue/splitbutton';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Steps from 'primevue/steps';
import StyleClass from 'primevue/styleclass';
import TabMenu from 'primevue/tabmenu';
import Tag from 'primevue/tag';
import TieredMenu from 'primevue/tieredmenu';
import Textarea from 'primevue/textarea';
import Timeline from 'primevue/timeline';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Toolbar from 'primevue/toolbar';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tooltip from 'primevue/tooltip';
import ToggleButton from 'primevue/togglebutton';
import Tree from 'primevue/tree';
import TreeSelect from 'primevue/treeselect';
import TreeTable from 'primevue/treetable';
import TriStateCheckbox from 'primevue/tristatecheckbox';

import CodeHighlight from '@/utilities/AppCodeHighlight';
import BlockViewer from '@/components/BlockViewer';
import pinia from '@/utilities/pinia';

import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue';
import BeatLoader from 'vue-spinner/src/BeatLoader.vue';
import PacmanLoader from 'vue-spinner/src/PacmanLoader.vue';
import MoonLoader from 'vue-spinner/src/MoonLoader.vue';
import FadeLoader from 'vue-spinner/src/FadeLoader.vue';
import NProgress from 'nprogress';
import Treeselect from 'vue3-treeselect';

import * as Sentry from "@sentry/vue";

// Custom inputs
import BaseAutoComplete from './modules/custom-inputs/BaseAutoComplete.vue';
import BaseBreadCrumb from './modules/custom-inputs/BaseBreadCrumb.vue';
import BaseCalendarInput from './modules/custom-inputs/BaseCalendarInput.vue';
import BaseDropdown from './modules/custom-inputs/BaseDropdown.vue';
import BaseEditor from './modules/custom-inputs/BaseEditor.vue';
import BaseMultiSelect from './modules/custom-inputs/BaseMultiSelect.vue';
import BaseNumberInput from './modules/custom-inputs/BaseNumberInput.vue';
import BaseSelectionInput from './modules/custom-inputs/BaseSelectionInput.vue';
import BaseTextInput from './modules/custom-inputs/BaseTextInput.vue';
import CustomChip from './modules/custom-inputs/CustomChip.vue';
import CustomTag from './modules/custom-inputs/CustomTag.vue';
import CustomTreeSelect from './modules/custom-inputs/CustomTreeSelect.vue';
import CustomVueTreeSelect from './modules/custom-inputs/CustomVueTreeSelect.vue';
import EntityBreadcrumbTreeSelect from './product/ams/modules/task/components/EntityBreadcrumbTreeSelect.vue';
import LoaderDialog from './components/LoaderDialog.vue';
// Custom Base Charts
import AmsBaseChart from './modules/amscharts/BaseChart.vue';
// Custom inputs

import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import 'prismjs/themes/prism-coy.css';
import 'nprogress/nprogress.css';
import 'vue3-treeselect/dist/vue3-treeselect.css';

const app = createApp(AppWrapper);

app.use(pinia);

app.config.globalProperties.$appState = reactive({ isRTL: false, isNewThemeLoaded: false, layoutMode: 'light' });

app.use(PrimeVue, { ripple: true });
app.use(ConfirmationService);
app.use(ToastService);
app.use(router);

Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DNS,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled.
    tracePropagationTargets: [
        "localhost",
        "https://ams-staging.ignistechnologies.co.uk",
        "https://ams-demo.ignistechnologies.co.uk",
        "https://ams.ignistechnologies.co.uk",
    ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);
app.directive('code', CodeHighlight);
app.directive('badge', BadgeDirective);
app.directive('styleclass', StyleClass);

app.component('Accordion', Accordion);
app.component('AccordionTab', AccordionTab);
app.component('autocomplete', autocomplete);
app.component('Avatar', Avatar);
app.component('AvatarGroup', AvatarGroup);
app.component('Badge', Badge);
app.component('Breadcrumb', Breadcrumb);
app.component('Button', Button);
app.component('Calendar', Calendar);
app.component('Card', Card);
app.component('Carousel', Carousel);
app.component('Chart', Chart);
app.component('Checkbox', Checkbox);
app.component('Chip', Chip);
app.component('Chips', Chips);
app.component('ColorPicker', ColorPicker);
app.component('Column', Column);
app.component('ColumnGroup', ColumnGroup);
app.component('ConfirmDialog', ConfirmDialog);
app.component('ConfirmPopup', ConfirmPopup);
app.component('ContextMenu', ContextMenu);
app.component('DataTable', DataTable);
app.component('DataView', DataView);
app.component('DataViewLayoutOptions', DataViewLayoutOptions);
app.component('Dialog', Dialog);
app.component('Divider', Divider);
app.component('Dropdown', Dropdown);
app.component('Editor', Editor);
app.component('Fieldset', Fieldset);
app.component('FileUpload', FileUpload);
app.component('Galleria', Galleria);
app.component('Image', Image);
app.component('InlineMessage', InlineMessage);
app.component('Inplace', Inplace);
app.component('InputMask', InputMask);
app.component('InputNumber', InputNumber);
app.component('InputSwitch', InputSwitch);
app.component('InputText', InputText);
app.component('Knob', Knob);
app.component('Listbox', Listbox);
app.component('MegaMenu', MegaMenu);
app.component('Menu', Menu);
app.component('Menubar', Menubar);
app.component('Message', Message);
app.component('MultiSelect', MultiSelect);
app.component('OrderList', OrderList);
app.component('OrganizationChart', OrganizationChart);
app.component('OverlayPanel', OverlayPanel);
app.component('Paginator', Paginator);
app.component('Panel', Panel);
app.component('PanelMenu', PanelMenu);
app.component('Password', Password);
app.component('PickList', PickList);
app.component('ProgressBar', ProgressBar);
app.component('ProgressSpinner', ProgressSpinner);
app.component('RadioButton', RadioButton);
app.component('Rating', Rating);
app.component('Row', Row);
app.component('SelectButton', SelectButton);
app.component('ScrollPanel', ScrollPanel);
app.component('ScrollTop', ScrollTop);
app.component('Slider', Slider);
app.component('Sidebar', Sidebar);
app.component('Skeleton', Skeleton);
app.component('SpeedDial', SpeedDial);
app.component('SplitButton', SplitButton);
app.component('Splitter', Splitter);
app.component('SplitterPanel', SplitterPanel);
app.component('Steps', Steps);
app.component('TabMenu', TabMenu);
app.component('TabView', TabView);
app.component('TabPanel', TabPanel);
app.component('Tag', Tag);
app.component('Textarea', Textarea);
app.component('TieredMenu', TieredMenu);
app.component('Timeline', Timeline);
app.component('Toast', Toast);
app.component('Toolbar', Toolbar);
app.component('ToggleButton', ToggleButton);
app.component('Tree', Tree);
app.component('TreeSelect', TreeSelect);
app.component('TreeTable', TreeTable);
app.component('TriStateCheckbox', TriStateCheckbox);

app.component('BlockViewer', BlockViewer);
app.component('PulseLoader', PulseLoader);
app.component('ScaleLoader', ScaleLoader);
app.component('VueTreeSelect', Treeselect);
app.component('BeatLoader', BeatLoader);
app.component('PacmanLoader', PacmanLoader);
app.component('MoonLoader', MoonLoader);
app.component('FadeLoader', FadeLoader);

app.component('BaseAutoComplete', BaseAutoComplete);
app.component('BaseBreadCrumb', BaseBreadCrumb);
app.component('BaseCalendarInput', BaseCalendarInput);
app.component('BaseDropdown', BaseDropdown);
app.component('BaseEditor', BaseEditor);
app.component('BaseMultiSelect', BaseMultiSelect);
app.component('BaseNumberInput', BaseNumberInput);
app.component('BaseSelectionInput', BaseSelectionInput);
app.component('BaseTextInput', BaseTextInput);
app.component('CustomChip', CustomChip);
app.component('CustomTag', CustomTag);
app.component('CustomTreeSelect', CustomTreeSelect);
app.component('CustomVueTreeSelect', CustomVueTreeSelect);
app.component('EntityBreadcrumbTreeSelect', EntityBreadcrumbTreeSelect);
app.component('LoaderDialog', LoaderDialog);
// Custom Base Charts
app.component('AmsBaseChart', AmsBaseChart);

// custom changes starts here
app.config.globalProperties.$showToast = function (severity, summary, detail) {
    const toast = {
        severity: severity ?? 'error',
        summary: summary ?? 'Error',
        detail: detail ?? 'Something went wrong',
        life: 5000,
    };
    this.$toast.add(toast);
};

EventBus.on('show-toast', (res) => {
    app.config.globalProperties.$showToast(res.severity, res.summary, res.detail);
});

// configuring Nprogress
NProgress.configure({
    trickle: true,
    showSpinner: true,
    easing: 'ease',
    speed: 200,
});

// warnings disable
const ignoredErrors = ['Property "$createElement" was accessed during render', 'Failed to resolve component: transition-group', 'Failed to resolve component: transition'];

app.config.warnHandler = (msg, vm, trace) => {
    const shouldIgnoreError = ignoredErrors.some((error) => msg.includes(error));
    // Check if the warning is related to the $createElement, transition and transition-property
    if (shouldIgnoreError) {
        // Suppress the warning
        return;
    }

    // For other warnings, you can log them or handle them as needed
    console.warn(msg, vm, trace);
};

app.mount('#app');
